// ------------------------------------------------------------
// buildSheets/index.js
// ------------------------------------------------------------
// ONLY CREATES NEW SHEETS
// NEVER TOUCHES EXPENSES
// INCOME AND DRIVER PERIODS ONLY
// ------------------------------------------------------------

import { createEmptySheet, addBankIncomeToSheet, addCashTripToSheet, finalizeSheetTotals } from "../helpers";
import { WEEK_KEYS, getCarSplitsForDriverWeek, getCarSplitsForDriverDateRange, getCarForDriverByDate, loadAssignmentsMap } from "../../../earningsImport/assignmentUtils";
import { loadRideHailingWeekPeriods, getConfiguredWeekRange } from "../../../earningsImport/rideHailingWeekPeriodUtils";
import { loadDriverPlanMap, getDriverPlanForDate } from "../../driverPlanHistory";
import { loadAllDriversMap } from "../../driverData";
import { addAssignmentPeriods, findMergedPeriod } from "./assignmentPeriods";
import { saveOrphanedIncome } from "../orphanedIncomeUtils";

// ------------------------------------------------------------
// SAFE DATE NORMALIZER
// ------------------------------------------------------------
function safeDate(v) {
  if (!v) return null;

  if (v instanceof Date) return v;
  if (v.toDate) return v.toDate();
  if (v.seconds !== undefined) return new Date(v.seconds * 1000);
  if (v._seconds !== undefined) return new Date(v._seconds * 1000);

  if (typeof v === "string") return new Date(v);

  try {
    return new Date(v);
  } catch {
    return null;
  }
}
function clampEndDate(realEnd, periodEnd) {
  if (!realEnd) return periodEnd;
  const re = realEnd instanceof Date ? realEnd : safeDate(realEnd);
  const pe = periodEnd instanceof Date ? periodEnd : safeDate(periodEnd);
  return re > pe ? pe : re;
}
function clampStartDate(realStart, periodStart) {
  if (!realStart) return periodStart;

  const rs = realStart instanceof Date ? realStart : safeDate(realStart);
  const ps = periodStart instanceof Date ? periodStart : safeDate(periodStart);

  if (!rs) return ps;
  if (!ps) return rs;

  // if assignment started before this sheet period, clamp to periodStart
  return rs < ps ? ps : rs;
}

// ------------------------------------------------------------
// SAFE FORMAT
// ------------------------------------------------------------
function safeFormat(d) {
  const v = safeDate(d);
  if (!v || isNaN(v.getTime())) return "";
  return (
    v.getFullYear() +
    "-" +
    String(v.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(v.getDate()).padStart(2, "0")
  );
}

// ------------------------------------------------------------
// KEY
// ------------------------------------------------------------
export function makeSheetKey(carNumber, month, periodStart, periodEnd) {
  return `${carNumber}|${month}|${safeFormat(periodStart)}|${safeFormat(periodEnd)}`;
}

// ------------------------------------------------------------
// WEEK
// ------------------------------------------------------------
function getWeekKeyFromDateString(dateStr) {
  if (!dateStr) return null;
  const day = Number(dateStr.split("-")[2] || 0);
  if (day <= 7) return "week1";
  if (day <= 14) return "week2";
  if (day <= 21) return "week3";
  if (day <= 28) return "week4";
  return "week5";
}

function getWeekStartDate(monthStr, weekKey) {
  const idx = WEEK_KEYS.indexOf(String(weekKey || "").toLowerCase());
  if (idx === -1) return null;
  const [year, month] = String(monthStr).split("-").map(Number);
  const startDay = [1, 8, 15, 22, 29][idx];
  return new Date(year, month - 1, startDay, 0, 0, 0, 0);
}

function maxDate(a, b) {
  const da = safeDate(a);
  const db = safeDate(b);
  if (!da) return db;
  if (!db) return da;
  return da > db ? da : db;
}


function parseSheetId(sheetId = "") {
  const parts = String(sheetId || "").split("|");
  if (parts.length < 4) return null;
  return {
    carNumber: parts[0],
    month: parts[1],
    periodStart: new Date(`${parts[2]}T00:00:00`),
    periodEnd: new Date(`${parts[3]}T23:59:59`),
  };
}

function normalizeCar(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function resolveAssignedSheetTarget(row, month, sheetsByKey, assignmentPeriods, fallbackDate) {
  const parsed = parseSheetId(row.assignedToSheetId);
  const carNumber =
    row.assignedToSheetCarNumber ||
    row.assignedToCar ||
    parsed?.carNumber ||
    "";

  const periodStart =
    safeDate(row.assignedToSheetPeriodStart) ||
    safeDate(parsed?.periodStart);
  const periodEnd =
    safeDate(row.assignedToSheetPeriodEnd) ||
    safeDate(parsed?.periodEnd);

  if (carNumber && periodStart && periodEnd) {
    const key = makeSheetKey(carNumber, month, periodStart, periodEnd);
    return { carNumber, periodStart, periodEnd, key, matchedBy: "assignedToSheetId" };
  }

  // Legacy fallback for older assigned orphaned rows that only stored car number.
  if (row.assignedToCar) {
    const refDate = safeDate(fallbackDate) || getWeekStartDate(month, row.week) || null;
    const merged = findMergedPeriod(row.assignedToCar, refDate, assignmentPeriods);
    if (merged) {
      const key = makeSheetKey(row.assignedToCar, month, merged.periodStart, merged.periodEnd);
      return {
        carNumber: row.assignedToCar,
        periodStart: merged.periodStart,
        periodEnd: merged.periodEnd,
        key,
        matchedBy: "assignedToCarLegacy",
      };
    }
  }

  // Final safety fallback: if a sheet id was saved but car spacing changed,
  // find an existing sheet with same car text and same period.
  if (parsed?.carNumber && parsed?.periodStart && parsed?.periodEnd) {
    const parsedStart = safeFormat(parsed.periodStart);
    const parsedEnd = safeFormat(parsed.periodEnd);
    const existingKey = Object.keys(sheetsByKey || {}).find((key) => {
      const sh = sheetsByKey[key];
      return (
        normalizeCar(sh?.carNumber) === normalizeCar(parsed.carNumber) &&
        safeFormat(sh?.periodStart) === parsedStart &&
        safeFormat(sh?.periodEnd) === parsedEnd
      );
    });

    if (existingKey) {
      const sh = sheetsByKey[existingKey];
      return {
        carNumber: sh.carNumber,
        periodStart: sh.periodStart,
        periodEnd: sh.periodEnd,
        key: existingKey,
        matchedBy: "existingSheetPeriod",
      };
    }
  }

  return null;
}

// ------------------------------------------------------------
// DRIVER PERIODS (standard shape)
// driverPeriods: { [driverId]: [{ driverId, driverName, startDate, endDate }] }
// ------------------------------------------------------------
function addDriverPeriod(sheet, { driverId, driverName, startDate, endDate }) {
  if (!driverId) return;

  if (!sheet.driverPeriods) sheet.driverPeriods = {};
  if (!Array.isArray(sheet.driverPeriods[driverId])) sheet.driverPeriods[driverId] = [];

  const s = safeDate(startDate);
  const e = safeDate(endDate);
  if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return;

  const name = (driverName || "").trim();

  // keep sheet.drivers clean + unique
  if (name) {
    if (!Array.isArray(sheet.drivers)) sheet.drivers = [];
    if (!sheet.drivers.some((x) => String(x).trim().toLowerCase() === name.toLowerCase())) {
      sheet.drivers.push(name);
    }
  }

  // add then merge overlaps/adjacent
  const list = [
    ...sheet.driverPeriods[driverId],
    { driverId, driverName: name, startDate: s, endDate: e },
  ].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const merged = [];
  for (const p of list) {
    const last = merged[merged.length - 1];
    if (!last) {
      merged.push(p);
      continue;
    }

    const lastEnd = new Date(last.endDate);
    const curStart = new Date(p.startDate);

    // overlap OR adjacent day
    const adjacentOrOverlap = curStart <= new Date(lastEnd.getTime() + 24 * 60 * 60 * 1000);

    if (adjacentOrOverlap) {
      last.endDate = new Date(
        Math.max(new Date(last.endDate).getTime(), new Date(p.endDate).getTime())
      );
      if (!last.driverName && p.driverName) last.driverName = p.driverName;
    } else {
      merged.push(p);
    }
  }

  sheet.driverPeriods[driverId] = merged;
}

// ------------------------------------------------------------
// MAIN
// ------------------------------------------------------------
export async function buildSheets({
  month,
  weeklyEarnings,
  manualTrips,
  posTransactions,
  existingSheetKeys = new Set(),
}) {
  console.log("🚀 buildSheets() FUNCTION STARTED!");

  // Track counts
  let orphanedWeeklyCount = 0;
  let orphanedManualCount = 0;
  let orphanedTotalAmount = 0;
  let regularWeeklyCount = 0;
  let regularManualCount = 0;

  // Count orphaned vs regular
  weeklyEarnings.forEach((d) => {
    if (d.isOrphaned) orphanedWeeklyCount++;
    else regularWeeklyCount++;
  });

  manualTrips.forEach((t) => {
    if (t.isOrphaned) orphanedManualCount++;
    else regularManualCount++;
  });

  console.log(`📊 Processing: ${regularWeeklyCount} regular + ${orphanedWeeklyCount} orphaned weekly earnings`);
  console.log(`📊 Processing: ${regularManualCount} regular + ${orphanedManualCount} orphaned manual trips`);

  // Safety: if an orphaned item is manually assigned later, do not add it
  // when the original raw weekly upload is already present in this rebuild.
  // This prevents duplicates like: raw income + old orphaned copy.
  function weeklySignature(row = {}) {
    const original = row.originalData || {};
    const driverId = String(row.driverId || original.driverId || "").trim();
    const platform = String(row.platform || original.platform || "").trim().toLowerCase();
    const week = String(row.week || row.weekKey || original.week || original.weekKey || "").trim().toLowerCase();
    const batch = String(row.uploadBatchId || original.uploadBatchId || "").trim();
    const sourceDriverId = String(row.sourceDriverId || original.sourceDriverId || "").trim();
    return [driverId, platform, week, batch || sourceDriverId].join("|");
  }

  // Manual assignment should override the normal raw weekly row for the same upload.
  // Otherwise the assigned orphan can be skipped or duplicated while the original raw row
  // is still present in driverWeeklyEarnings.
  const assignedOrphanedWeeklySignatures = new Set(
    (weeklyEarnings || [])
      .filter((row) => row.isOrphaned && row.status === "assigned" && (row.assignedToSheetId || row.assignedToCar))
      .map(weeklySignature)
      .filter((key) => key.replace(/\|/g, "").trim())
  );

  const [year, m] = month.split("-").map(Number);
  const monthStart = new Date(year, m - 1, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, m, 0, 23, 59, 59, 999);

  const assignmentsMap = await loadAssignmentsMap();

  let rideHailingWeekConfig = null;
  try {
    rideHailingWeekConfig = await loadRideHailingWeekPeriods(month);
    console.log("✅ Loaded ride-hailing week periods", rideHailingWeekConfig?.weeks || {});
  } catch (weekConfigError) {
    console.error("❌ Failed to load ride-hailing week periods; using calendar weeks", weekConfigError);
    rideHailingWeekConfig = null;
  }

  // ⭐⭐ ADD THIS DEBUGGING CODE ⭐⭐
  console.log("🔄 BUILD SHEETS: Loaded assignments for", Object.keys(assignmentsMap).length, "drivers");
  // Log a few sample assignments
  const sampleDriverIds = Object.keys(assignmentsMap).slice(0, 3);
  sampleDriverIds.forEach((driverId) => {
    const assignments = assignmentsMap[driverId];
    console.log(`📋 Driver ${driverId} has ${assignments.length} assignments:`);
    assignments.forEach((a, i) => {
      console.log(
        `   ${i + 1}. Car: ${a.vehicleNumber}, Company: ${a.company}, Dates: ${a.startDate.toISOString().split("T")[0]} - ${a.endDate ? a.endDate.toISOString().split("T")[0] : "ongoing"}`
      );
    });
  });

  const planMap = await loadDriverPlanMap();
  const driversMap = await loadAllDriversMap();

  const sheetsByKey = {};

  // ------------------------------------------------------------
  // 1) MERGED PERIODS
  // ------------------------------------------------------------
  const assignmentPeriods = addAssignmentPeriods(assignmentsMap, monthStart, monthEnd);

  // ------------------------------------------------------------
  // 1B) CREATE NEW SHEETS (PERIOD BASED)
  // ------------------------------------------------------------
  for (const p of assignmentPeriods) {
    const key = makeSheetKey(p.carNumber, month, p.periodStart, p.periodEnd);

    // ✅ Prevent ghost sheets: only build a period sheet if at least one driver
    // has a REAL assignment overlapping this period
    const hasRealDriver = (p.driverIds || []).some((driverId) => {
      if (!driverId) return false;
      return (assignmentsMap[driverId] || []).some(
        (a) =>
          a.vehicleNumber === p.carNumber &&
          a.startDate <= p.periodEnd &&
          (!a.endDate || a.endDate >= p.periodStart)
      );
    });

    if (!hasRealDriver) {
      console.warn(
        `⛔ Skipping empty period sheet for ${p.carNumber} ${safeFormat(p.periodStart)} → ${safeFormat(p.periodEnd)} (no real driver)`
      );
      continue;
    }

    if (!sheetsByKey[key]) {
      const sheet = createEmptySheet({
        carNumber: p.carNumber,
        carModel: p.carModel,
        company: p.company,

        // ⭐ driverCompany must ALWAYS follow car company
        driverCompany: p.company,

        month,
        periodStart: p.periodStart,
        periodEnd: p.periodEnd,
        planType: "UNKNOWN",
      });

      sheet.periodStart = p.periodStart;
      sheet.periodEnd = p.periodEnd;
      sheet.periodLabel = `${safeFormat(p.periodStart)} → ${safeFormat(p.periodEnd)}`;

      sheet.driverPeriods = {};

      // ⭐ PRE-FILL DRIVER PERIODS BASED ONLY ON ASSIGNMENTS — NOT INCOME
      (p.driverIds || []).forEach((driverId) => {
        const assignments = assignmentsMap[driverId] || [];

        // Find REAL assignment for this car during this period
        const real = assignments.find(
          (a) =>
            a.vehicleNumber === p.carNumber &&
            a.startDate <= p.periodEnd &&
            (!a.endDate || a.endDate >= p.periodStart)
        );

        if (!real) return; // Driver is NOT assigned inside this sheet → skip

        // Resolve driver name
        const drv = driversMap[driverId] || {};
        const name =
          drv.fullName ||
          drv.name ||
          drv.driverName ||
          p.driverNames?.find((n) => n) ||
          "Unknown";

        // Assign date range for UI
        addDriverPeriod(sheet, {
          driverId,
          driverName: name,
          startDate: clampStartDate(real.startDate, p.periodStart),
          endDate: clampEndDate(real.endDate, p.periodEnd),
        });

        // Add driver to driver list only once
        if (!sheet.drivers.includes(name)) {
          sheet.drivers.push(name);
        }
      });

      // --------------------------------------------------------
      // Detect plan + add only VALID drivers (FIXED - NO DUPLICATES)
      // --------------------------------------------------------
      let detectedPlan = "FIXED";
      const processedDriverIds = new Set(); // Track processed drivers

      for (let idx = 0; idx < (p.driverIds || []).length; idx++) {
        const driverId = p.driverIds[idx];
        const assignments = assignmentsMap[driverId] || [];

        // Skip if already processed for this sheet
        if (processedDriverIds.has(driverId)) {
          console.log(`⏭️ Skipping duplicate driver ${driverId} for car ${p.carNumber}`);
          continue;
        }

        // REAL assignment validation
        const real = assignments.find(
          (a) =>
            a.vehicleNumber === p.carNumber &&
            a.startDate <= p.periodEnd &&
            (!a.endDate || a.endDate >= p.periodStart)
        );

        // ❌ If no valid assignment → skip ONLY this driver
        if (!real) {
          console.warn(
            `⛔ Driver ${driverId} has NO VALID assignment for period ${safeFormat(
              p.periodStart
            )} → ${safeFormat(p.periodEnd)}`
          );
          continue;
        }

        // Mark as processed
        processedDriverIds.add(driverId);

        // Determine plan type
        const refDate = clampStartDate(real.startDate, p.periodStart);
        const pt = getDriverPlanForDate({
          driverId,
          date: refDate,
          planMap,
        });

        if (pt === "SALARY") detectedPlan = "SALARY";

        // Resolve driver name
        const d = driversMap[driverId] || {};
        const name =
          (d.fullName || d.name || d.driverName || "").trim() ||
          (p.driverNames[idx] || "").trim();

        // Check if this driver already exists in driverPeriods
        const existingDriverEntry = sheet.driverPeriods[driverId];

        if (!existingDriverEntry) {
          // Only add if not already in driverPeriods

          // Add driver name to array (if not already there)
          if (name && !sheet.drivers.includes(name)) {
            sheet.drivers.push(name);
          }

          // Save REAL assignment period
          const realStart = clampStartDate(real.startDate, p.periodStart);
          const realEnd = clampEndDate(real.endDate, p.periodEnd);

          addDriverPeriod(sheet, {
            driverId,
            driverName: name,
            startDate: realStart,
            endDate: realEnd,
          });

          console.log(`✅ Added driver ${name} (${driverId}) to ${p.carNumber}`);
        } else {
          console.log(`⏭️ Driver ${name} already exists in driverPeriods, skipping duplicate`);
        }
      }

      // After processing all drivers, apply detected plan
      sheet.planType = detectedPlan;

      // ✅ FINAL GUARD: if no drivers survived validation, do NOT save this sheet
      if (!sheet.driverPeriods || Object.keys(sheet.driverPeriods).length === 0) {
        console.warn(
          `⛔ Not saving empty sheet for ${p.carNumber} ${safeFormat(p.periodStart)} → ${safeFormat(p.periodEnd)}`
        );
        continue;
      }

      sheetsByKey[key] = sheet;
    }
  }

  // ------------------------------------------------------------
  // 2) WEEKLY EARNINGS (SPLIT BY MID-WEEK ASSIGNMENT CHANGES)
  // ------------------------------------------------------------
  for (const d of weeklyEarnings || []) {
    const weekKey = (d.week || "").toLowerCase();
    if (!WEEK_KEYS.includes(weekKey)) continue;

    const driverId = d.driverId;
    if (!driverId) continue;

    const weekIndex = WEEK_KEYS.indexOf(weekKey) + 1;
    const configuredWeekRange = getConfiguredWeekRange(rideHailingWeekConfig, weekKey, month);

    if (!d.isOrphaned && assignedOrphanedWeeklySignatures.has(weeklySignature(d))) {
      console.warn(
        `⏭️ SKIPPED RAW WEEKLY because a manually assigned orphaned copy is the source of truth: ${d.driverName || driverId}, ${d.platform || "platform"}, ${weekKey}`
      );
      continue;
    }

    // ✅ Manually assigned orphaned weekly income: push directly to the exact selected built sheet.
    // New rows should have assignedToSheetId. assignedToCar remains as legacy fallback.
    if (d.isOrphaned && (d.assignedToSheetId || d.assignedToCar)) {
      const fallbackRefDate = maxDate(configuredWeekRange?.start, monthStart) || monthStart;
      const target = resolveAssignedSheetTarget(d, month, sheetsByKey, assignmentPeriods, fallbackRefDate);
      if (!target) {
        console.warn(
          `⚠️ Assigned orphaned weekly income could not find selected sheet/car for ${d.assignedToCar || d.assignedToSheetId || "unknown"} in ${weekKey}`
        );
        continue;
      }

      const sheetKeyStr = target.key;
      if (!sheetsByKey[sheetKeyStr]) {
        const sheet = createEmptySheet({
          carNumber: target.carNumber,
          carModel: d.assignedToSheetCarModel || "",
          company: d.assignedToSheetCompany || d.company || "",
          month,
          periodStart: target.periodStart,
          periodEnd: target.periodEnd,
          planType: d.assignedToSheetPlanType || "UNKNOWN",
        });
        sheet.periodStart = target.periodStart;
        sheet.periodEnd = target.periodEnd;
        sheet.periodLabel = `${safeFormat(target.periodStart)} → ${safeFormat(target.periodEnd)}`;
        sheet.driverPeriods = {};
        sheetsByKey[sheetKeyStr] = sheet;
      }

      const sheet = sheetsByKey[sheetKeyStr];
      const driverName = d.driverName || "Unknown";

      if (driverId) {
        addDriverPeriod(sheet, {
          driverId,
          driverName,
          startDate: target.periodStart,
          endDate: target.periodEnd,
        });
      }

      const bankAmt = Number(d.bankIncome) || 0;
      const cashAmt = Number(d.cashIncome) || 0;

      if (bankAmt) {
        addBankIncomeToSheet(sheet, {
          platform: (d.platform || "OTHERS").toUpperCase(),
          driverId,
          driverName,
          weekKey,
          amount: bankAmt,
        });
      }

      if (cashAmt) {
        addCashTripToSheet(sheet, {
          platform: (d.platform || "OTHERS").toUpperCase(),
          driverId,
          driverName,
          weekKey,
          amount: cashAmt,
        });
      }

      continue;
    }

    // ✅ get per-car day splits for this week (handles custom ride-hailing week periods)
    const splits = configuredWeekRange
      ? getCarSplitsForDriverDateRange({
          driverId,
          rangeStart: configuredWeekRange.start,
          rangeEnd: configuredWeekRange.end,
          assignmentsMap,
        })
      : getCarSplitsForDriverWeek({ driverId, month, weekIndex, assignmentsMap });

    if (!splits || splits.length === 0) {
      orphanedWeeklyCount++;
      const totalBank = Number(d.bankIncome) || 0;
      const totalCash = Number(d.cashIncome) || 0;
      const amount = totalBank + totalCash;
      orphanedTotalAmount += amount;

      if (!d.isOrphaned) {
        await saveOrphanedIncome(month, "weeklyEarnings", {
          driverId,
          driverName: d.driverName || "Unknown",
          platform: d.platform || "Unknown",
          company: d.company || "",
          week: weekKey,
          bankIncome: totalBank,
          cashIncome: totalCash,
          amount,
          originalData: d,
        });
      }

      console.warn(`⛔ ORPHANED WEEKLY: Driver ${driverId} (${d.driverName || "No Name"})`);
      console.warn(`   Platform: ${d.platform || "Unknown"}, Week: ${weekKey}, Amount: ${amount} AED`);
      continue;
    }

    const totalBank = Number(d.bankIncome) || 0;
    const totalCash = Number(d.cashIncome) || 0;

    // Split helper (2-decimal safe, sum(parts)=total)
    const splitAmount = (total, ratios) => {
      const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
      let remaining = round2(total);
      const parts = [];
      ratios.forEach((r, i) => {
        if (i === ratios.length - 1) {
          parts.push(remaining);
        } else {
          const part = round2(total * (Number(r) || 0));
          parts.push(part);
          remaining = round2(remaining - part);
        }
      });
      return parts;
    };

    const ratios = splits.map((s) => s.ratio || 0);
    const bankParts = totalBank ? splitAmount(totalBank, ratios) : ratios.map(() => 0);
    const cashParts = totalCash ? splitAmount(totalCash, ratios) : ratios.map(() => 0);

    // ✅ NEW: if all splits fail merged-period lookup, save as orphan instead of losing silently
    let weeklyAppliedToAtLeastOneSheet = false;

    splits.forEach((split, idx) => {
      const carNumber = split.vehicleNumber;
      if (!carNumber) return;

      const refDate = maxDate(split.startDate, monthStart) || monthStart;
      const merged = findMergedPeriod(carNumber, refDate, assignmentPeriods);

      if (!merged) {
        console.warn(
          `⚠️ WEEKLY SPLIT FOUND BUT NO MERGED PERIOD: Driver ${driverId}, Car ${carNumber}, Week ${weekKey}`
        );
        return;
      }

      const sheetKeyStr = makeSheetKey(carNumber, month, merged.periodStart, merged.periodEnd);

      if (!sheetsByKey[sheetKeyStr]) {
        const sheet = createEmptySheet({
          carNumber,
          carModel: split.vehicleType || "",
          company: split.company || "",
          month,
          periodStart: merged.periodStart,
          periodEnd: merged.periodEnd,
          planType:
            getDriverPlanForDate({
              driverId,
              date: refDate,
              planMap,
            }) || "UNKNOWN",
        });

        sheet.periodStart = merged.periodStart;
        sheet.periodEnd = merged.periodEnd;
        sheet.periodLabel = `${safeFormat(merged.periodStart)} → ${safeFormat(merged.periodEnd)}`;
        sheet.driverPeriods = {};
        sheetsByKey[sheetKeyStr] = sheet;
      }

      const sheet = sheetsByKey[sheetKeyStr];
      const driverName = split.driverName || d.driverName || "Unknown";

      // Add / merge driver period precisely for the overlap days
      addDriverPeriod(sheet, {
        driverId,
        driverName,
        startDate: clampStartDate(split.startDate, merged.periodStart),
        endDate: clampEndDate(split.endDate, merged.periodEnd),
      });

      // BANK
      const bankAmt = bankParts[idx] || 0;
      if (bankAmt) {
        addBankIncomeToSheet(sheet, {
          platform: (d.platform || "OTHERS").toUpperCase(),
          driverId,
          driverName,
          weekKey,
          amount: bankAmt,
        });
      }

      // CASH
      const cashAmt = cashParts[idx] || 0;
      if (cashAmt) {
        addCashTripToSheet(sheet, {
          platform: (d.platform || "OTHERS").toUpperCase(),
          driverId,
          driverName,
          weekKey,
          amount: cashAmt,
        });
      }

      if (bankAmt || cashAmt) {
        weeklyAppliedToAtLeastOneSheet = true;
      }
    });

    // ✅ If splits existed but none could be applied to any valid merged period, save orphan
    if (!weeklyAppliedToAtLeastOneSheet) {
      orphanedWeeklyCount++;
      const amount = totalBank + totalCash;
      orphanedTotalAmount += amount;

      if (!d.isOrphaned) {
        await saveOrphanedIncome(month, "weeklyEarnings", {
          driverId,
          driverName: d.driverName || "Unknown",
          platform: d.platform || "Unknown",
          company: d.company || "",
          week: weekKey,
          bankIncome: totalBank,
          cashIncome: totalCash,
          amount,
          originalData: d,
        });
      }

      console.warn(`⛔ ORPHANED WEEKLY AFTER SPLIT CHECK: Driver ${driverId} (${d.driverName || "No Name"})`);
      console.warn(`   Platform: ${d.platform || "Unknown"}, Week: ${weekKey}, Amount: ${amount} AED`);
    }
  }

  // ------------------------------------------------------------
  // 3) MANUAL TRIPS
  // ------------------------------------------------------------

  // Safe date normalizer (unchanged)
  function normalizeDateString(input) {
    if (!input) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input; // YYYY-MM-DD
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(input)) {
      // DD-MM-YYYY
      const [d, m, y] = input.split("-");
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(input)) {
      // DD/MM/YYYY
      const [d, m, y] = input.split("/");
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }

    try {
      const d = new Date(input);
      if (!isNaN(d)) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate()
        ).padStart(2, "0")}`;
      }
    } catch (e) {}

    return null;
  }

  for (const t of manualTrips || []) {
    console.log("🟨 Manual trip loaded:", {
      driver_raw: t.driver || t.driverName || t.manualDriverName,
      driverIdResolved: t.driverIdResolved,
      driverId: t.driverId,
      batchDate: t.batchDate,
      pickupDate: t.pickupDate,
      driverFinalPrice: t.driverFinalPrice,
      totalDriver: t.totalDriver,
    });

    // ------------------------------------------
    // STEP 1 — SAFELY GET DATE
    // ------------------------------------------
    let dateStr =
      normalizeDateString(t.pickupDate) ||
      normalizeDateString(t.batchDate);

    if (!dateStr) {
      console.warn("⚠️ Could not parse date for manual trip:", t);
      continue;
    }

    const weekKey = getWeekKeyFromDateString(dateStr);
    if (!WEEK_KEYS.includes(weekKey)) continue;

    const tripDate = new Date(dateStr);
    tripDate.setHours(0, 0, 0, 0);

    // ------------------------------------------
    // STEP 2 — RESOLVE DRIVER ID
    // ------------------------------------------
    let driverId = t.driverIdResolved || t.driverId;

    // If UID missing — find by name
    if (!driverId) {
      const nameToFind =
        t.driverName ||
        t.manualDriverName ||
        t.driver;

      if (nameToFind) {
        for (const [id, d] of Object.entries(driversMap)) {
          const full = d.fullName?.toLowerCase() || "";
          const nm = d.name?.toLowerCase() || "";
          const dn = d.driverName?.toLowerCase() || "";

          const tripName = nameToFind.toLowerCase();

          if (
            full.includes(tripName) ||
            nm.includes(tripName) ||
            dn.includes(tripName) ||
            tripName.includes(full)
          ) {
            driverId = id;
            console.log(`✅ Resolved driver: "${nameToFind}" → ${driverId}`);
            break;
          }
        }
      }
    }

    if (!driverId) {
      console.warn("⚠️ Skipping manual trip: No driver ID resolved", t);
      continue;
    }

    // ------------------------------------------
    // STEP 3 — PLAN TYPE
    // ------------------------------------------
    const planType =
      getDriverPlanForDate({
        driverId,
        date: tripDate,
        planMap,
      }) || "SALARY";

    // ------------------------------------------
    // STEP 4 — CALCULATE AMOUNT
    // ------------------------------------------
    let amount = 0;

    if (planType === "FIXED") {
      amount = Number(t.totalDriver || 0);
    } else {
      amount = Number(t.driverFinalPrice || 0);
    }

    if (!amount) {
      console.warn("⚠️ Manual trip missing payable amount:", { planType, t });
      continue;
    }

    // ------------------------------------------
    // STEP 5 — FIND ASSIGNMENT
    // ------------------------------------------
    let assignment = null;
    let manualAssignedTarget = null;

    if (t.isOrphaned && (t.assignedToSheetId || t.assignedToCar)) {
      manualAssignedTarget = resolveAssignedSheetTarget(t, month, sheetsByKey, assignmentPeriods, tripDate);
      if (manualAssignedTarget) {
        assignment = {
          vehicleNumber: manualAssignedTarget.carNumber,
          vehicleType: t.assignedToSheetCarModel || "",
          company: t.assignedToSheetCompany || t.assignedCompany || "",
        };
      }
    } else {
      assignment = getCarForDriverByDate({
        driverId,
        date: dateStr,
        assignmentsMap,
      });
    }

    // ⭐⭐ FIX 1: DEFINE driverName BEFORE USING IT ⭐⭐
    const officialName =
      driversMap[driverId]?.fullName ||
      driversMap[driverId]?.name ||
      driversMap[driverId]?.driverName;

    const driverName = officialName || "Unknown";

    // ⭐⭐ FIX 2: NOW ADD ORPHANED INCOME CHECK ⭐⭐
    if (!assignment || !assignment.vehicleNumber) {
      // ⭐⭐ SAVE TO FIRESTORE ⭐⭐

      // Check if this is already orphaned
      if (!t.isOrphaned) {
        // Only save to Firestore if it's NEW orphaned income
        await saveOrphanedIncome(month, "manualTrips", {
          driverId,
          driverName: driverName,
          date: dateStr,
          amount,
          planType,
          originalData: t,
        });

        console.warn(`⛔ ORPHANED MANUAL SAVED: Driver ${driverId}, Amount: ${amount} AED`);
      } else {
        console.warn(`⛔ ORPHANED MANUAL (already in Firestore): Driver ${driverId}, Amount: ${amount} AED`);
      }

      continue; // Skip this income
    }

    const carNumber = assignment.vehicleNumber;

    const merged = manualAssignedTarget || findMergedPeriod(carNumber, tripDate, assignmentPeriods);
    if (!merged) {
      console.warn(`⚠️ No merged period found for ${carNumber} on ${dateStr}`);
      continue;
    }

    const sheetKeyStr = merged.key || makeSheetKey(
      carNumber,
      month,
      merged.periodStart,
      merged.periodEnd
    );

    // ------------------------------------------
    // STEP 6 — CREATE SHEET IF NOT EXISTS
    // ------------------------------------------
    if (!sheetsByKey[sheetKeyStr]) {
      const sheet = createEmptySheet({
        carNumber,
        carModel: assignment.vehicleType || "",
        company: assignment.company,
        driverCompany: assignment.company,
        month,
        periodStart: merged.periodStart,
        periodEnd: merged.periodEnd,
        planType,
      });

      sheet.periodLabel = `${safeFormat(merged.periodStart)} → ${safeFormat(
        merged.periodEnd
      )}`;

      sheet.driverPeriods = {};
      sheetsByKey[sheetKeyStr] = sheet;
    }

    const sheet = sheetsByKey[sheetKeyStr];

    // ------------------------------------------
    // STEP 7 — DRIVER NAME + REAL ASSIGNMENT CHECK
    // ------------------------------------------

    // 1️⃣ Only allow driver if REAL assignment exists, unless this orphan was manually assigned
    const real = t.isOrphaned && (t.assignedToSheetId || t.assignedToCar)
      ? {
          startDate: merged.periodStart,
          endDate: merged.periodEnd,
        }
      : (assignmentsMap[driverId] || []).find(
          (a) =>
            a.vehicleNumber === carNumber &&
            a.startDate <= merged.periodEnd &&
            (!a.endDate || a.endDate >= merged.periodStart)
        );

    if (!real) {
      console.warn(
        `⛔ Manual trip: Driver ${driverId} not assigned to ${carNumber} during ${safeFormat(
          merged.periodStart
        )} → ${safeFormat(merged.periodEnd)}`
      );
      continue;
    }

    // 2️⃣ Now add driver (AFTER validation) - Check for duplicates
    const existingDriverEntry = sheet.driverPeriods[driverId];

    if (!existingDriverEntry) {
      // Only add if not already in driverPeriods
      if (!sheet.drivers.includes(driverName)) sheet.drivers.push(driverName);

      // 3️⃣ Record assignment details
      const realStart = clampStartDate(real.startDate, merged.periodStart);
      const realEnd = clampEndDate(real.endDate, merged.periodEnd);

      addDriverPeriod(sheet, {
        driverId,
        driverName,
        startDate: realStart,
        endDate: realEnd,
      });
    } else {
      console.log(`⏭️ Manual trip: Driver ${driverName} already in sheet, skipping duplicate`);
    }

    // ------------------------------------------
    // STEP 8 — ADD INCOME (FIXED)
    // ------------------------------------------
    const platform = (t.clientName || t.clientId || "OTHER CLIENT")
      .toUpperCase()
      .trim();

    addBankIncomeToSheet(sheet, {
      platform,
      driverId,
      driverName,
      driverCompany: assignment.company,
      weekKey,
      amount,
    });

    console.log(
      `➕ Added manual trip: ${driverName} | ${carNumber} | ${dateStr} | ${amount} AED | ${platform} | PLAN=${planType}`
    );
  }

  // ------------------------------------------------------------
  // 4) POS TRANSACTIONS (FIXED - DON'T ADD AS DRIVER)
  // ------------------------------------------------------------
  for (const p of posTransactions || []) {
    const { terminalId, carNumber, company, vehicleType, weekKey, amount, date } = p;

    if (!carNumber || !weekKey || !amount) continue;

    // 1️⃣ Find correct merged period for THIS transaction date
    const merged = findMergedPeriod(carNumber, new Date(date), assignmentPeriods);
    if (!merged) {
      console.warn(`⚠ No merged period found for POS on ${date} for ${carNumber}`);
      continue;
    }

    // 2️⃣ Correct sheet key
    const sheetKeyStr = makeSheetKey(
      carNumber,
      month,
      merged.periodStart,
      merged.periodEnd
    );

    // 3️⃣ Create sheet if missing
    if (!sheetsByKey[sheetKeyStr]) {
      const sheet = createEmptySheet({
        carNumber,
        carModel: vehicleType || "",
        company: company || "",
        driverCompany: company || "", // ⭐ required
        month,
        periodStart: merged.periodStart,
        periodEnd: merged.periodEnd,
        planType: "UNKNOWN",
      });

      sheet.periodStart = merged.periodStart;
      sheet.periodEnd = merged.periodEnd;
      sheet.periodLabel = `${safeFormat(merged.periodStart)} → ${safeFormat(
        merged.periodEnd
      )}`;

      sheet.driverPeriods = {};

      sheetsByKey[sheetKeyStr] = sheet;
    }

    const sheet = sheetsByKey[sheetKeyStr];

    // ⭐⭐ FIX: DON'T add POS as a driver, just create a driver name for the income row
    const driverName = `POS DEVICE (${terminalId})`;

    // ⭐⭐ REMOVED: if (!sheet.drivers.includes(driverName)) sheet.drivers.push(driverName);

    // Finally add the POS amount into bank income
    addBankIncomeToSheet(sheet, {
      platform: "POS",
      driverName, // This creates the income row but doesn't add to drivers array
      driverCompany: sheet.company,
      weekKey,
      amount,
    });
  }

  // ------------------------------------------------------------
  // FINAL
  // ------------------------------------------------------------
  const sheets = Object.values(sheetsByKey);

  // ------------------------------------------------------------
  // FIX A — REMOVE DUPLICATE DRIVER PERIODS FOR SAME DRIVER
  // ------------------------------------------------------------
  for (const sheet of sheets) {
    const cleaned = {};
    for (const driverId in sheet.driverPeriods || {}) {
      if (!cleaned[driverId]) {
        cleaned[driverId] = sheet.driverPeriods[driverId];
      } else {
        console.warn(
          `🟡 Duplicate driver assignment removed: ${driverId} in car ${sheet.carNumber}`
        );
      }
    }
    sheet.driverPeriods = cleaned;

    /* ⭐⭐ COMMENT OUT START
    // ------------------------------------------------------------
    // FIX B — REMOVE DRIVERS WHO HAVE ZERO INCOME (NO ACTIVITY)
    // ------------------------------------------------------------
    const driversToKeep = new Set();

    // Check incomeRows
    (sheet.incomeRows || []).forEach((r) => {
      if (r.driverId) driversToKeep.add(r.driverId);
      // Also check if this is a POS row (no driverId but has driverName)
      if (!r.driverId && r.driverName && r.driverName.includes('POS DEVICE')) {
        // For POS rows, we need to keep the driver if they exist
        // But POS shouldn't be in drivers array anyway, so this might not matter
      }
    });

    // Check cashRows
    (sheet.cashRows || []).forEach((r) => {
      if (r.driverId) driversToKeep.add(r.driverId);
    });

    // If driver exists in driverPeriods but NOT in driversToKeep → REMOVE
    for (const driverId of Object.keys(sheet.driverPeriods || {})) {
      if (!driversToKeep.has(driverId)) {
        console.warn(
          `⛔ Removing driver ${driverId} from ${sheet.carNumber} — NO income`
        );
        delete sheet.driverPeriods[driverId];
      }
    }

    // Also update sheet.drivers (names array)
    sheet.drivers = sheet.drivers.filter((name) => {
      // keep only names whose driverId still exists
      return Object.values(sheet.driverPeriods).some(
        (dp) => dp.driverName === name
      );
    });
    ⭐⭐ COMMENT OUT END */
  }

  sheets.forEach((s) => finalizeSheetTotals(s));

  sheets.sort((a, b) => {
    if (String(a.carNumber) !== String(b.carNumber)) {
      return String(a.carNumber).localeCompare(String(b.carNumber));
    }
    return (a.periodStart || 0) - (b.periodStart || 0);
  });

  sheets.forEach((sheet) => {
    sheet.id = makeSheetKey(sheet.carNumber, month, sheet.periodStart, sheet.periodEnd);
  });

  // ⭐⭐ DEBUG: CHECK FOR CAR 27471 Private L
  console.log("=".repeat(50));
  console.log("🔍 DEBUG: buildSheets() FINAL OUTPUT");
  console.log("=".repeat(50));

  const testSheet = sheets.find((s) => s.carNumber === "27471 Private L");
  if (testSheet) {
    console.log("📊 27471 Private L FROM buildSheets():", {
      drivers: testSheet.drivers,
      driverPeriods: testSheet.driverPeriods
        ? Object.values(testSheet.driverPeriods).map((periods) => {
            const list = Array.isArray(periods) ? periods : [periods];
            const first = list[0] || {};
            const last = list[list.length - 1] || {};
            return {
              name: first.driverName,
              start: first.startDate || first.start,
              end: last.endDate || last.end,
            };
          })
        : [],
      hasPOSInDrivers: testSheet.drivers?.some((d) =>
        String(d).toUpperCase().includes("POS")
      ),
      incomeRowsDrivers: testSheet.incomeRows
        ?.filter((r) => r.rowType === "data")
        .map((r) => r.driver)
        .filter(Boolean),
    });
  } else {
    console.log("❌ 27471 Private L NOT FOUND in buildSheets output");
  }
  console.log("=".repeat(50));

  // ⭐⭐ ADD ORPHANED INCOME SUMMARY ⭐⭐
  if (orphanedWeeklyCount > 0 || orphanedManualCount > 0) {
    console.log("========================================");
    console.log("⚠️  ORPHANED INCOME SUMMARY");
    console.log(`📅 Month: ${month}`);
    console.log(`📊 Weekly Earnings: ${orphanedWeeklyCount} items`);
    console.log(`📊 Manual Trips: ${orphanedManualCount} items`);
    console.log(`💰 Total Amount: ${orphanedTotalAmount} AED`);
    console.log("========================================");

    // Store in localStorage for UI to display
    if (typeof window !== "undefined") {
      localStorage.setItem(
        `orphaned_${month}`,
        JSON.stringify({
          weekly: orphanedWeeklyCount,
          manual: orphanedManualCount,
          total: orphanedTotalAmount,
          timestamp: new Date().toISOString(),
        })
      );
    }
  }

  return sheets;
}